import { memo, useState, useEffect } from 'react';
import { Box, Flex, useMediaQuery, Button } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getSelectedPage } from 'redux/DashboardSliceV2';
import { FetchModelBlocks, getBlocks, GetUserModels, resetBlock } from 'redux/ModelsSlice';
import { fetchAllScenarios } from 'redux/ScenarioSlice';
import { handleBuilderModeTab } from 'redux/BuilderModeSlice';
import { useBloxModelSectionTitle } from 'hooks/useBloxDocumentTitle';
import GridMenuIcon from 'components/Icons/GridMenuIcon';
import { UserModalNew } from 'pages/UserManagement/ProfileNewUIRevamp';

import LogoSection from './components/LogoSection';
import NavigationBar from './components/NavigationBar';
import UserActions from './components/UserActions';
import MobileNavigationMenu from './components/MobileNavigationMenu';
import TabContent from './components/TabContent';
import useActiveTab from './hooks/useActiveTab';
import useModelDashboardBootstrap from './hooks/useModelDashboardBootstrap';
import { HEADER_CONFIG, MODEL_TAB_CONFIG } from './constants';

const ModalOverViewPageHeaderRevamp = () => {
  const { modelId } = useParams();
  const dispatch: any = useDispatch();
  const model = useSelector(getBlocks);
  const selectedPage = useSelector(getSelectedPage);
  const { activeTab, setActiveTab } = useActiveTab('dashboard');
  useModelDashboardBootstrap(true);
  const [isPhone] = useMediaQuery('(max-width: 767px)');
  const [isIpad] = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const sectionLabel =
    MODEL_TAB_CONFIG.find((tab) => tab.tabType === activeTab)?.name ?? 'Dashboards';
  const dashboardPageName =
    activeTab === 'dashboard' && selectedPage?.name ? String(selectedPage.name) : undefined;
  useBloxModelSectionTitle(sectionLabel, model?.name, dashboardPageName);

  useEffect(() => {
    dispatch(GetUserModels({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(FetchModelBlocks({ modelId: Number(modelId), loader: true }));
    if (modelId) {
      dispatch(fetchAllScenarios(Number(modelId)));
    }
  }, [modelId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(handleBuilderModeTab(0));
      dispatch(resetBlock());
    };
  }, [dispatch]);

  const showTabletMenu = Boolean(isPhone);
  const headerGridTemplateColumns: string = showTabletMenu || isIpad ? '1fr auto' : '1fr auto 1fr';

  return (
    <Box bg='linear-gradient(180deg, #FCFAF8 14.669%, #F5F2FF 48.801%, #FFF0FC 100%)' minH='100vh'>
      <Box
        display='flex'
        flexDirection='column'
        w='100%'
        minH={HEADER_CONFIG.HEIGHT}
        position='relative'
        zIndex={4000}
        px={3}
        // Symmetric, so the bar's height is exactly padding + content + border
        // and the controls sit optically centred.
        py='6px'
        alignItems='center'
        justifyContent='space-between'
        bg={HEADER_CONFIG.BG_COLOR}
        gap='6px'
        // Every size in this bar is the design's value already multiplied by
        // the 0.9 this box used to apply via `zoom`. `zoom` is non-standard and
        // scales *everything* — padding, borders, fonts — while the layout
        // outside it has no idea, so each value had to be pre-divided by 0.9 to
        // come out right (px 13.33 for 12, border 1.12 for 1) and the declared
        // 53px height actually occupied 47.7px, which TabContent's
        // `calc(100vh - 53px)` then got wrong. Real values, no correction
        // factors, and the height below is now what it says it is.
        borderBottom='1px solid #D1D6DD'
      >
        <Box
          display='grid'
          gridTemplateColumns={headerGridTemplateColumns}
          alignItems='center'
          gap={2}
          width='100%'
        >
          <Box justifySelf='start' minW={0}>
            <LogoSection modelName={model?.name} modelData={model} />
          </Box>
          {!showTabletMenu && !isIpad && (
            <Box display='flex' justifyContent='center'>
              <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} />
            </Box>
          )}
          <Flex alignItems='center' gap={2} justifySelf='end'>
            {showTabletMenu && (
              <Button
                variant='solid'
                bg='white'
                borderRadius='full'
                width='36px'
                height='36px'
                minW='36px'
                p={0}
                _hover={{ bg: 'gray.50' }}
                aria-label='Open navigation menu'
                onClick={() => setIsMenuOpen(true)}
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <GridMenuIcon width={20} height={20} color='#6B5FC6' />
              </Button>
            )}
            {!showTabletMenu && <UserActions />}
          </Flex>
        </Box>
        {isIpad && (
          <Box display='flex' alignItems='center' justifyContent='center' px={6} pb={2}>
            <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} />
          </Box>
        )}
      </Box>

      <TabContent
        activeTab={activeTab}
        blocks={model?.blocks}
        categories={model?.categories}
        isLoadingBlocks={model == null}
      />

      {showTabletMenu && (
        <MobileNavigationMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      )}

      <UserModalNew />
    </Box>
  );
};

export default memo(ModalOverViewPageHeaderRevamp);
